import { gql } from "@apollo/client";

import coreValueAssessmentQuestionFragment from "@apps/assessments/graphql/core-value-assessment-question-fragment";
import CommonGoalArtifactFragment from "@graphql/common-goal-artifact-fragment";

export default gql`
  ${CommonGoalArtifactFragment}
  ${coreValueAssessmentQuestionFragment}
  fragment EngagementSurveyDeliveryAssessmentGroupSection on AssessmentSectionInterface {
    id
    templateContextId
    title
    description
    subjectVisibility
    questions {
      edges {
        node {
          id
          templateContextId
          question {
            templateContextId
            ... on RangeAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on IndividualGoalAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              goal {
                ...CommonGoalArtifactFragment
              }
            }
            ... on OverallGoalAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on IndividualRecognitionCoreValueAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              coreValue {
                ...AssessmentQuestionCoreValue
              }
            }
            ... on OverallRecognitionCoreValueAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on CompetencyAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              role {
                id
                title
                level
              }
              competency {
                id
                title
                criteria {
                  edges {
                    node {
                      id
                      content
                    }
                  }
                }
              }
            }
            ... on CompetencyCriteriaAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              role {
                id
                title
                level
              }
              competencyCriteria {
                id
                content
                competency {
                  id
                  title
                  description
                }
              }
            }
            ... on ResponsibilityAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              responsibility {
                id
                content
                role {
                  id
                  title
                  level
                }
              }
            }
            ... on TalentIndicatorAssessmentQuestionNode {
              id
              title
              description
              startValue
              endValue
              labels
              labelDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
              talentIndicator {
                id
                talentIndicatorType
              }
            }
            ... on MultiChoiceAssessmentQuestionNode {
              id
              title
              description
              options
              optionDescriptions
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on TextAssessmentQuestionNode {
              id
              title
              description
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
            ... on NpsAssessmentQuestionNode {
              id
              title
              description
              comment
              isResponseMandatory
              responses
              questionType
              responseVisibility
            }
          }
        }
      }
    }
  }

  fragment EngagementSurveyDelivery on AssessmentDeliveryNode {
    id
    state
    deliveryDatetime
    dueDate
    periodStartDate
    periodEndDate
    canUpdate {
      permission
      reason
    }
    complianceProgram {
      id
      title
      hasAccessToAppliesToUsers
      periodStartDate
      periodEndDate
      canReadDetails {
        permission
        reason
      }
      engagementSurveyTemplate {
        id
        assessmentType
        anonymity
        questionSets {
          edges {
            node {
              id
              templateContextId
              title
              sections {
                edges {
                  node {
                    ...EngagementSurveyDeliveryAssessmentGroupSection
                  }
                }
              }
            }
          }
        }
      }
    }
    answers(
      teamIds: $teamIds
      managerIds: $managerIds
      managerIdsFullTree: $managerIdsFullTree
      employeeStartDateRange: $employeeStartDateRange
      locations: $locations
    ) {
      edges {
        node {
          ... on RangeAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            integerAnswer
            comment
          }
          ... on IndividualGoalAssessmentAnswerNode {
            id
            questionId
          }
          ... on OverallGoalAssessmentAnswerNode {
            id
            questionId
          }
          ... on IndividualRecognitionCoreValueAssessmentAnswerNode {
            id
            questionId
          }
          ... on OverallRecognitionCoreValueAssessmentAnswerNode {
            id
            questionId
          }
          ... on ResponsibilityAssessmentAnswerNode {
            id
            questionId
          }
          ... on TalentIndicatorAssessmentAnswerNode {
            id
            questionId
          }
          ... on CompetencyAssessmentAnswerNode {
            id
            questionId
          }
          ... on CompetencyCriteriaAssessmentAnswerNode {
            id
            questionId
          }
          ... on MultiChoiceAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            choices
            comment
          }
          ... on TextAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            textAnswer
            comment
          }
          ... on NpsAssessmentAnswerNode {
            id
            responder {
              id
              name
              email
              firstName
              avatar
            }
            questionId
            integerAnswer
            npsValue
            comment
          }
        }
      }
    }
    groupedAnswers(
      groupBy: $groupBy
      teamIds: $teamIds
      managerIds: $managerIds
      managerIdsFullTree: $managerIdsFullTree
      employeeStartDateRange: $employeeStartDateRange
      locations: $locations
      collapsedKeys: $collapsedKeys
    ) {
      groupKey
      groupLabel
      rollupType
      questionId
      answerIds
      numberResponders
      numberComments
      isHidden
      isCollapsed
    }
  }

  query getDeliveredEngagementSurvey(
    $assessmentDeliveryId: IntOrString!
    $teamIds: [Int]
    $managerIds: [Int]
    $managerIdsFullTree: [Int]
    $employeeStartDateRange: [String]
    $locations: [String]
    $groupBy: EngagementSurveyReportGroupBy
    $collapsedKeys: [String]
  ) {
    assessmentDelivery(id: $assessmentDeliveryId) {
      ...EngagementSurveyDelivery
    }
  }
`;
